import React, { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  Label,
  Select,
  Spinner,
  Text,
} from "@fluentui/react-components";
import { apiUrl } from "../../utils/api";
import { PermissionGrid } from "./PermissionGrid";
import { TeamRole } from "./permissionConstants";
import { useBestaetigen } from "../redesign/bestaetigen";

type UserOption = {
  id: string;
  firstName?: string;
  lastName?: string;
  username?: string;
  name?: string;
  email?: string;
};

type Props = {
  open: boolean;
  users: UserOption[];
  onClose: () => void;
  onCreated: () => void;
};

const userLabel = (u: UserOption) => {
  const composed = [u.firstName, u.lastName].filter(Boolean).join(" ").trim();
  return composed || u.name || u.email || u.id;
};

export const TeamCreateDialog: React.FC<Props> = ({ open, users, onClose, onCreated }) => {
  const { hinweisen } = useBestaetigen();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [memberRoles, setMemberRoles] = useState<Record<string, TeamRole>>({});
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setName("");
      setDescription("");
      setMemberRoles({});
      setSelected(new Set());
    }
  }, [open]);

  const toggleMember = (userId: string, checked: boolean) => {
    setMemberRoles((prev) => {
      const next = { ...prev };
      if (checked) next[userId] = next[userId] ?? "teammitglied";
      else delete next[userId];
      return next;
    });
  };

  const setMemberRole = (userId: string, role: TeamRole) => {
    setMemberRoles((prev) => ({ ...prev, [userId]: role }));
  };

  const togglePerm = (perm: string, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(perm);
      else next.delete(perm);
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      const createRes = await fetch(apiUrl("rbac/teams"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          description: description.trim() || undefined,
        }),
      });
      if (!createRes.ok) {
        const err = await createRes.json().catch(() => ({}));
        void hinweisen({ titel: "Team nicht angelegt", text: err?.message || "Das Team konnte nicht angelegt werden.", ton: "danger" });
        setSubmitting(false);
        return;
      }
      const team = await createRes.json();
      const teamId = team?.id as string;
      if (!teamId) throw new Error("No team id returned");

      const memberEntries = Object.entries(memberRoles);
      for (const [userId, teamRole] of memberEntries) {
        await fetch(apiUrl(`rbac/teams/${teamId}/members`), {
          method: "POST",
            headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId, teamRole }),
        });
      }

      if (selected.size > 0) {
        await fetch(apiUrl(`rbac/teams/${teamId}/permissions`), {
          method: "PUT",
            headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ permissions: Array.from(selected) }),
        });
      }

      onCreated();
      onClose();
    } catch (e: any) {
      void hinweisen({ titel: "Team nicht angelegt", text: e?.message || "Beim Anlegen des Teams ist ein Fehler aufgetreten.", ton: "danger" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface style={{ maxWidth: 720 }}>
        <DialogBody>
          <DialogTitle>Neues Team anlegen</DialogTitle>
          <DialogContent style={{ display: "flex", flexDirection: "column", gap: 16, paddingTop: 16 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <Label htmlFor="ct-name" required>Team Name</Label>
              <Input id="ct-name" value={name} onChange={(_, d) => setName(d.value)} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <Label htmlFor="ct-desc">Beschreibung</Label>
              <Input id="ct-desc" value={description} onChange={(_, d) => setDescription(d.value)} placeholder="Optional" />
            </div>

            <div>
              <Text weight="semibold">Mitglieder zuweisen</Text>
              <div style={{ marginTop: 8, maxHeight: 200, overflowY: "auto", border: "1px solid #eee", borderRadius: 4 }}>
                {users.length === 0 ? (
                  <Text size={200} style={{ padding: 8, display: "block" }}>Keine Benutzer verfügbar.</Text>
                ) : (
                  users.map((u) => {
                    const checked = !!memberRoles[u.id];
                    return (
                      <div
                        key={u.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          padding: "6px 10px",
                          borderBottom: "1px solid #f1f1f1",
                        }}
                      >
                        <Checkbox checked={checked} onChange={(_, d) => toggleMember(u.id, !!d.checked)} />
                        <span style={{ flex: 1 }}>{userLabel(u)}</span>
                        <Select
                          value={memberRoles[u.id] ?? "teammitglied"}
                          disabled={!checked}
                          onChange={(_, d) => setMemberRole(u.id, d.value as TeamRole)}
                        >
                          <option value="teammitglied">Teammitglied</option>
                          <option value="teamleiter">Teamleiter</option>
                        </Select>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            <div>
              <Text weight="semibold">Berechtigungen des Teams</Text>
              <Text size={200} style={{ display: "block", color: "#666", marginBottom: 6 }}>
                Welche Aktionen Teammitglieder auf Datensätzen ihres Teams ausführen dürfen.
              </Text>
              <PermissionGrid selected={selected} onToggle={togglePerm} />
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>Abbrechen</Button>
            <Button appearance="primary" onClick={handleSubmit} disabled={submitting || !name.trim()}>
              {submitting ? <Spinner size="tiny" /> : "Erstellen"}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default TeamCreateDialog;
